import { closestHTMLElement } from "../../lib/dom.js";

function populateSessionEditPanel(order, deps) {
  requestAnimationFrame(() => {
    deps.openSessionAddPanel();
    deps.refreshSessionAddModeText();
    const targetItem = deps.getState().activeWorkout?.items?.[String(order)];
    const setsInput = document.getElementById("sessionAddSets");
    const restInput = document.getElementById("sessionAddRestSec");
    if (setsInput instanceof HTMLInputElement && targetItem?.setsPlan) {
      setsInput.value = String(targetItem.setsPlan);
    }
    if (restInput instanceof HTMLInputElement && targetItem?.restSec) {
      restInput.value = String(targetItem.restSec);
    }
    if (targetItem?.exerciseId) {
      deps.setSessionAddSelectedExercise(targetItem.exerciseId, targetItem.nameSnapshot || "");
    } else {
      deps.setSessionAddSelectedExercise("", "");
    }
    deps.clearSessionAddSearchResults();
  });
}

export async function handleSessionClick(target, deps) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const moreBtn = closestHTMLElement(target, ".session-item-more-btn");
  if (moreBtn) {
    deps.toggleSessionItemContextMenu(moreBtn.dataset.order);
    return true;
  }

  if (target.id === "openSessionCancelModalBtn") {
    deps.openSessionCancelModal();
    return true;
  }

  if (target.id === "resumeWorkoutBtn") {
    await deps.openSession(target.dataset.workoutId);
    return true;
  }

  if (target.id === "startManualWorkoutBtn") {
    await deps.handleStartManualWorkout();
    return true;
  }

  if (target.id === "openManualWorkoutImportModalBtn") {
    await deps.openProgramDayImportModal({
      importTarget: "manual-workout",
    });
    return true;
  }

  if (target.dataset.action === "dismiss-session-inline-notice") {
    deps.clearSessionInlineNotice(String(deps.getState().activeWorkout?.workoutId || "").trim());
    return true;
  }

  if (
    target.id === "sessionConflictModalCloseBtn" ||
    target.id === "sessionConflictModal"
  ) {
    deps.closeSessionConflictModal();
    return true;
  }

  if (target.id === "sessionConflictModalResumeBtn") {
    deps.closeSessionConflictModal("resume", { restoreFocus: false });
    return true;
  }

  if (target.id === "confirmReplaceActiveSessionBtn") {
    deps.closeSessionConflictModal("replace", { restoreFocus: false });
    return true;
  }

  if (
    target.id === "sessionNoticeModalCloseBtn" ||
    target.id === "sessionNoticeModalConfirmBtn" ||
    target.id === "sessionNoticeModal"
  ) {
    deps.closeSessionNoticeModal();
    return true;
  }

  if (
    target.id === "sessionCancelModalCloseBtn" ||
    target.id === "sessionCancelModalKeepBtn" ||
    target.id === "sessionCancelModal"
  ) {
    deps.closeSessionCancelModal();
    return true;
  }

  if (
    target.id === "sessionFinishModalCloseBtn" ||
    target.id === "sessionFinishModalKeepBtn" ||
    target.id === "sessionFinishModal"
  ) {
    deps.closeSessionFinishModal();
    return true;
  }

  if (target.id === "closeSessionBtn" || target.id === "closeSessionNavBtn") {
    deps.closeSession();
    return true;
  }

  if (target.id === "confirmCancelSessionBtn") {
    await deps.handleCancelActiveSession();
    return true;
  }

  if (target.id === "confirmFinishWorkoutBtn") {
    const workoutId =
      deps.els.sessionFinishModal instanceof HTMLElement
        ? deps.els.sessionFinishModal.dataset.workoutId
        : deps.getState().activeWorkout?.workoutId;
    if (workoutId) {
      await deps.handleFinishWorkout(workoutId);
    }
    return true;
  }

  if (target.id === "finishWorkoutBtn") {
    deps.requestFinishWorkout(target.dataset.workoutId);
    return true;
  }

  if (target.id === "finishWorkoutNavBtn") {
    const workoutId = target.dataset.workoutId || deps.getState().activeWorkout?.workoutId;
    if (workoutId) {
      deps.requestFinishWorkout(workoutId);
    }
    return true;
  }

  if (target.id === "addSessionItemBtn") {
    await deps.handleAddSessionItem();
    return true;
  }

  if (target.id === "toggleSessionAddPanelBtn") {
    deps.handleToggleSessionAddPanel();
    return true;
  }

  if (target.id === "closeSessionAddPanelBtn") {
    deps.closeSessionAddPanel({ clearSelection: true });
    return true;
  }

  if (target.classList.contains("add-session-set-btn")) {
    await deps.handleAddSessionSet(target.dataset.order);
    return true;
  }

  if (target.classList.contains("add-drop-session-set-btn")) {
    const grid = closestHTMLElement(target, ".set-grid")
      || closestHTMLElement(target, ".session-item-card")?.querySelector(
        `.set-grid[data-order="${String(target.dataset.order || "").trim()}"][data-set-index="${String(target.dataset.setIndex || "").trim()}"]`,
      );
    if (grid) {
      await deps.handleAddDropSessionSet(grid);
    }
    return true;
  }

  if (target.dataset.action === "toggle-session-item-reorder") {
    deps.handleToggleSessionItemReorder(target.dataset.order);
    return true;
  }

  if (target.dataset.action === "close-session-item-reorder") {
    deps.handleCloseSessionItemReorder();
    return true;
  }

  if (target.classList.contains("change-session-item-btn")) {
    const safeOrder = Number(target.dataset.order);
    if (!Number.isInteger(safeOrder) || safeOrder < 0) {
      return true;
    }
    deps.closeSessionItemContextMenus();
    deps.closeSessionAddPanel({ clearSelection: true, syncHistory: false });
    deps.setState({
      sessionItemEditOrder: safeOrder,
      sessionItemReorderOrder: null,
    });
    populateSessionEditPanel(safeOrder, deps);
    return true;
  }

  if (target.dataset.action === "move-session-item") {
    await deps.handleMoveSessionItem(target.dataset.order, target.dataset.direction);
    return true;
  }

  if (target.classList.contains("cancel-session-item-change-btn")) {
    deps.closeSessionAddPanel({ clearSelection: true });
    deps.setState({
      sessionItemEditOrder: null,
    });
    return true;
  }

  if (target.classList.contains("remove-session-item-btn")) {
    deps.closeSessionItemContextMenus();
    await deps.handleRemoveSessionItem(target.dataset.order);
    return true;
  }

  if (target.id === "toggleRestTimerBtn" || target.id === "toggleRestEnabledBtn") {
    await deps.handleToggleRestTimer();
    return true;
  }

  if (target.id === "toggleRestTimerNavBtn") {
    await deps.handleRestNavButtonPress();
    return true;
  }

  if (target.dataset.action === "set-remove") {
    const grid = closestHTMLElement(target, ".set-grid");
    if (grid) {
      await deps.handleRemoveSessionSet(grid.dataset.order, grid.dataset.setIndex);
    }
    return true;
  }

  if (target.dataset.action === "set-done") {
    const grid = closestHTMLElement(target, ".set-grid");
    if (grid) {
      await deps.handleSetDone(grid);
    }
    return true;
  }

  return false;
}
